import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useCoffeeShopsMap, CoffeeShop, MapBounds } from '../hooks/useCoffeeShops';
import { createMapPinIcon, createClusterIcon } from '../components/MapPin';
import { CoffeeMapBasemap, COFFEE_MAP_CONTAINER_CLASS } from '../components/CoffeeMapBasemap';
import { RatingBadge } from '../components';
import { openMapDirections } from '../utils/mapLinks';
import { Button } from '../../../ui';

// component to fit map bounds to markers — only fires once after the initial
// load, so subsequent viewport-driven refetches don't keep yanking the camera.
const FitBounds: React.FC<{ shops: CoffeeShop[] }> = ({ shops }) => {
    const map = useMap();
    const didFitRef = React.useRef(false);

    useEffect(() => {
        if (didFitRef.current) return;
        if (shops.length > 0) {
            const bounds = L.latLngBounds(
                shops.map(shop => [shop.coordinates.lat, shop.coordinates.lng])
            );
            map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12, animate: false });
            didFitRef.current = true;
        }
    }, [shops, map]);

    return null;
};

// emit current viewport bounds to the parent on every pan/zoom, plus once on
// mount so the first fetch is viewport-scoped.
const ViewportReporter: React.FC<{ onBounds: (b: MapBounds) => void }> = ({ onBounds }) => {
    const reportBounds = (map: L.Map) => {
        const b = map.getBounds();
        const ne = b.getNorthEast();
        const sw = b.getSouthWest();
        onBounds([ne.lat, ne.lng, sw.lat, sw.lng]);
    };

    const map = useMapEvents({
        moveend: () => reportBounds(map),
        zoomend: () => reportBounds(map),
    });

    useEffect(() => {
        reportBounds(map);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return null;
};

interface ShopMapViewProps {
    onShopSelect?: (shopId: string) => void;
    shopTypeFilter?: string;
}

// default center (thessaloniki)
const DEFAULT_CENTER: [number, number] = [40.6401, 22.9444];

const ShopMapView: React.FC<ShopMapViewProps> = ({ onShopSelect, shopTypeFilter = 'all' }) => {
    const navigate = useNavigate();
    const { coffeeShops, loading, error, refetch, setBounds } = useCoffeeShopsMap({ shopType: shopTypeFilter });
    const [selectedShopId, setSelectedShopId] = useState<string | null>(null);

    const handleMarkerClick = (shopId: string) => {
        setSelectedShopId(shopId);
        if (onShopSelect) {
            onShopSelect(shopId);
        }
    };

    const handleViewDetails = (shopId: string) => {
        navigate(`/coffeeshops/${shopId}`);
    };

    const handleGetDirections = (shop: CoffeeShop) => {
        openMapDirections(shop.coordinates.lat, shop.coordinates.lng, shop.name);
    };

    // create custom cluster icon
    const createCustomClusterIcon = (cluster: any) => {
        const count = cluster.getChildCount();
        return L.icon({
            iconUrl: createClusterIcon(count),
            iconSize: [Math.min(50, 30 + Math.log(count) * 5), Math.min(50, 30 + Math.log(count) * 5)],
            iconAnchor: [Math.min(25, 15 + Math.log(count) * 2.5), Math.min(25, 15 + Math.log(count) * 2.5)]
        });
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-20 bg-paper/80 backdrop-blur-sm rounded-xl border border-ink/15">
                <div className="w-10 h-10 border-4 border-muted border-t-sky rounded-full animate-spin mb-4" />
                <p className="text-muted-foreground">Loading map...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center py-20 bg-paper/80 backdrop-blur-sm rounded-xl border border-ink/15">
                <h3 className="text-xl font-bold text-pink mb-2">Error loading map</h3>
                <p className="text-muted-foreground mb-4">{error}</p>
                <Button onClick={refetch} variant="soft">Try Again</Button>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* legend */}
            <div className="bg-paper/80 backdrop-blur-sm rounded-xl border border-ink/15 p-4 shadow-lg">
                <div className="flex flex-wrap items-center justify-center gap-6">
                    <span className="text-sm font-medium text-ink">
                        {coffeeShops.length} coffee shops
                    </span>
                    <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-1.5">
                            <div className="w-3 h-3 rounded-full bg-[#2bb941]" />
                            <span>8.0+</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <div className="w-3 h-3 rounded-full bg-[#f49f0c]" />
                            <span>5-7.9</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <div className="w-3 h-3 rounded-full bg-[#e54343]" />
                            <span>&lt;5</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* map container */}
            {coffeeShops.length === 0 ? (
                <div className="bg-paper/80 backdrop-blur-sm rounded-xl border border-ink/15 p-10 text-center shadow-lg">
                    <h3 className="text-xl font-bold text-ink mb-2">No coffee shops to display</h3>
                    <p className="text-muted-foreground">
                        Coffee shops will appear here after users rate coffees from those locations.
                    </p>
                </div>
            ) : (
                <div className="rounded-xl border border-ink/15 shadow-lg overflow-hidden relative z-0" style={{ height: '500px' }}>
                    <MapContainer
                        className={COFFEE_MAP_CONTAINER_CLASS}
                        center={DEFAULT_CENTER}
                        zoom={10}
                        style={{ height: '100%', width: '100%' }}
                        scrollWheelZoom={true}
                    >
                        <CoffeeMapBasemap />
                        <FitBounds shops={coffeeShops} />
                        <ViewportReporter onBounds={setBounds} />

                        <MarkerClusterGroup
                            chunkedLoading
                            iconCreateFunction={createCustomClusterIcon}
                            maxClusterRadius={50}
                            spiderfyOnMaxZoom={true}
                            showCoverageOnHover={false}
                        >
                            {coffeeShops.map(shop => {
                                const isSelected = selectedShopId === shop._id;
                                const size = isSelected ? 32 : 28;
                                const icon = L.icon({
                                    iconUrl: createMapPinIcon(shop.averageRating, isSelected),
                                    iconSize: [size, size],
                                    iconAnchor: [size / 2, size / 2],
                                    popupAnchor: [0, -size / 2]
                                });

                                return (
                                    <Marker
                                        key={shop._id}
                                        position={[shop.coordinates.lat, shop.coordinates.lng]}
                                        icon={icon}
                                        eventHandlers={{
                                            click: () => handleMarkerClick(shop._id)
                                        }}
                                    >
                                        <Popup>
                                            <div className="min-w-[200px] p-1">
                                                <h3 className="font-bold text-base text-ink mb-1">
                                                    {shop.name}
                                                </h3>
                                                <p className="text-sm text-muted-foreground mb-1">
                                                    {shop.address}
                                                </p>
                                                <div className="flex items-center gap-2 mb-3">
                                                    <RatingBadge rating={shop.averageRating} size="sm" showMax={false} />
                                                    <span className="text-xs text-muted-foreground">
                                                        {shop.totalRatings} ratings
                                                    </span>
                                                </div>
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => handleViewDetails(shop._id)}
                                                        className="flex-1 px-3 py-1.5 bg-ink text-paper text-sm font-semibold rounded-lg hover:bg-ink/80 transition-colors"
                                                    >
                                                        View
                                                    </button>
                                                    <button
                                                        onClick={() => handleGetDirections(shop)}
                                                        className="flex-1 px-3 py-1.5 bg-mint/80 text-ink text-sm font-semibold rounded-lg hover:bg-mint transition-colors"
                                                    >
                                                        Directions
                                                    </button>
                                                </div>
                                            </div>
                                        </Popup>
                                    </Marker>
                                );
                            })}
                        </MarkerClusterGroup>
                    </MapContainer>
                </div>
            )}
        </div>
    );
};

export default ShopMapView;
